import { Button, IconButton } from "@dropbox/dig-components/buttons";
// Tooltip provides additional exports that allows for standard styling of rich tooltips without overrides
// eslint-disable-next-line no-restricted-imports
import { Tooltip } from "@dropbox/dig-components/tooltips";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { OpenLine } from "@dropbox/dig-icons/assets";
import { AiStorageMini } from "@dropbox/dig-illustrations";
import { useDualMode } from "@mirage/service-experimentation/useDualMode";
import { useOnboardingValues } from "@mirage/service-onboarding/hooks";
import { openPersistentApp } from "@mirage/service-platform-actions";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import i18n from "@mirage/translations";
import * as React from "react";
import styles from "./OpenPersistentWindowButton.module.css";

export const OpenPersistentWindowButton = ({
  disableEducation = false,
}: {
  disableEducation?: boolean;
}) => {
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const { showLauncherTooltip } = useDualMode();
  const { getOnboardingValue, setOnboardingValue } = useOnboardingValues();
  const [openOnboardingTooltip, setOpenOnboardingTooltip] =
    React.useState(false);

  React.useEffect(() => {
    // Prevent UI trashing by disableEducation changing regularly
    const timer = setTimeout(() => {
      if (showLauncherTooltip && !disableEducation) {
        setOpenOnboardingTooltip(true);

        const updateSeenCount = async () => {
          const seenCount = await getOnboardingValue(
            "seenLauncherTooltipCount",
          );
          setOnboardingValue("seenLauncherTooltipCount", seenCount + 1);
        };
        updateSeenCount();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [showLauncherTooltip, disableEducation]);

  React.useEffect(() => {
    if (disableEducation) {
      setOpenOnboardingTooltip(false);
    }
  }, [disableEducation]);

  const handleCloseTooltip = () => {
    setOpenOnboardingTooltip(false);
    setOnboardingValue("dismissedLauncherTooltip", true);
  };

  const handleOpenPersistentApp = () => {
    openPersistentApp("launcher_button");
    handleCloseTooltip();
  };

  return (
    <>
      <Box display="flex" alignItems="center">
        <div className={styles.floatingAnchor} ref={triggerRef}></div>
        <DigTooltip
          title={i18n.t("open_persistent_app")}
          placement="bottom-end"
          isPortaled={true}
        >
          <IconButton
            variant="borderless"
            onClick={handleOpenPersistentApp}
            style={{ position: "relative" }}
            aria-label={i18n.t("open_persistent_app")}
          >
            <UIIcon src={OpenLine} />
          </IconButton>
        </DigTooltip>
      </Box>

      <DigTooltip.Control
        variant="rich"
        open={openOnboardingTooltip}
        placement="left-start"
        triggerRef={triggerRef}
        withIllustration={
          <Box paddingBottom="Micro Small">
            <AiStorageMini
              altText={i18n.t(
                "onboarding_dual_mode_rich_tooltip_illustration_alt",
              )}
            />
          </Box>
        }
      >
        <Tooltip.Title>
          {i18n.t("onboarding_dual_mode_rich_tooltip_title")}
        </Tooltip.Title>
        <Tooltip.Text>
          {i18n.t("onboarding_dual_mode_rich_tooltip_body")}
        </Tooltip.Text>
        <Tooltip.Actions>
          <Button variant="outline" onClick={handleCloseTooltip}>
            {i18n.t("got_it")}
          </Button>
        </Tooltip.Actions>
      </DigTooltip.Control>
    </>
  );
};
